import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
const CODE_GLYPHS = ['{', '}', '<', '/', '>', '0', '1', '+', '=', 'AI', 'UI', 'UX', 'API', 'DOM', 'CSS', 'JS', 'SDK', 'IDE', 'run', 'git'];
const LEGACY_EMITTER_X = 0.48;
const DESKTOP_MOTION_SHIFT_PX = 40;
const LEGACY_EMITTER_Y = [0.36, 0.42, 0.48, 0.54, 0.6];
const LEGACY_CORE_X = 0.632;
const LEGACY_CORE_Y = 0.535;
const MOBILE_BREAKPOINT = 720;
export function SectionHero() {
    const canvasRef = useRef(null);
    const heroRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const hero = heroRef.current;
        if (!canvas || !hero)
            return;
        const context = canvas.getContext('2d');
        if (!context)
            return;
        const ctx = context;
        let width = 0;
        let height = 0;
        let dpr = 1;
        let particles = [];
        let rafId;
        const layout = {
            emitterX: 0,
            emitterYs: [],
            coreX: 0,
            coreY: 0,
        };
        function useStageAnchoredEmitters() {
            return width <= MOBILE_BREAKPOINT;
        }
        function updateLayout() {
            if (width < 2 || height < 2)
                return;
            const heroRect = hero.getBoundingClientRect();
            const stage = hero.querySelector('.product-stage-motion');
            const core = hero.querySelector('.product-core');
            if (core) {
                const coreRect = core.getBoundingClientRect();
                const coreVisible = coreRect.width > 8 && coreRect.height > 8;
                if (coreVisible) {
                    layout.coreX = coreRect.left - heroRect.left + coreRect.width / 2;
                    layout.coreY = coreRect.top - heroRect.top + coreRect.height / 2;
                }
                else if (stage) {
                    const ui = hero.querySelector('.generated-ui');
                    if (ui) {
                        const uiRect = ui.getBoundingClientRect();
                        layout.coreX = uiRect.left - heroRect.left + uiRect.width * 0.22;
                        layout.coreY = uiRect.top - heroRect.top + uiRect.height * 0.5;
                    }
                    else {
                        const stageRect = stage.getBoundingClientRect();
                        layout.coreX = stageRect.left - heroRect.left + stageRect.width * 0.35;
                        layout.coreY = stageRect.top - heroRect.top + stageRect.height * 0.5;
                    }
                }
                else {
                    layout.coreX = width * LEGACY_CORE_X;
                    layout.coreY = height * LEGACY_CORE_Y;
                }
            }
            else {
                layout.coreX = width * LEGACY_CORE_X;
                layout.coreY = height * LEGACY_CORE_Y;
            }
            if (!useStageAnchoredEmitters()) {
                layout.emitterX = width * LEGACY_EMITTER_X - DESKTOP_MOTION_SHIFT_PX;
                layout.emitterYs = LEGACY_EMITTER_Y.map((r) => height * r);
                return;
            }
            if (!stage) {
                layout.emitterX = width * LEGACY_EMITTER_X;
                layout.emitterYs = LEGACY_EMITTER_Y.map((r) => height * r);
                return;
            }
            const stageRect = stage.getBoundingClientRect();
            const stageLeft = stageRect.left - heroRect.left;
            const stageTop = stageRect.top - heroRect.top;
            const stageW = stageRect.width;
            const stageH = stageRect.height;
            const label = stage.querySelector('.label-input');
            if (label) {
                const labelRect = label.getBoundingClientRect();
                layout.emitterX = labelRect.left - heroRect.left + labelRect.width * 0.5;
                const bandTop = labelRect.top - heroRect.top + labelRect.height * 0.5;
                const bandBottom = stageTop + stageH * 0.72;
                const span = Math.max(48, bandBottom - bandTop);
                layout.emitterYs = [0, 0.25, 0.5, 0.75, 1].map((r) => bandTop + span * r);
            }
            else {
                layout.emitterX = stageLeft + Math.min(56, stageW * 0.1);
                layout.emitterYs = [0.2, 0.36, 0.52, 0.68, 0.84].map((r) => stageTop + stageH * r);
            }
        }
        function resize() {
            const rect = hero.getBoundingClientRect();
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = rect.width;
            height = rect.height;
            canvas.width = Math.max(0, width * dpr);
            canvas.height = Math.max(0, height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            updateLayout();
            if (width >= 2 && height >= 2) {
                seedParticles();
            }
        }
        function getEmitter(index) {
            const ys = layout.emitterYs.length ? layout.emitterYs : [height * 0.5];
            return { x: layout.emitterX, y: ys[index % ys.length] };
        }
        function createParticle(index, spread = false) {
            const emitter = getEmitter(index);
            const { coreX, coreY } = layout;
            const progress = spread ? (index % 14) / 14 : 0;
            const jitter = ((index * 17) % 20) - 10;
            return {
                index,
                x: emitter.x + (coreX - emitter.x) * progress,
                y: emitter.y + (coreY - emitter.y) * progress + jitter,
                startX: emitter.x,
                startY: emitter.y + jitter,
                targetX: coreX,
                targetY: coreY + (((index * 13) % 36) - 18),
                speed: 0.00105 + (index % 5) * 0.00016,
                progress,
                size: 0.9 + ((index * 11) % 13) * 0.18,
                glyph: CODE_GLYPHS[index % CODE_GLYPHS.length],
                alpha: 0.48 + (index % 5) * 0.06,
                phase: index * 0.71,
            };
        }
        function seedParticles() {
            particles = Array.from({ length: 70 }, (_, i) => createParticle(i, true));
        }
        function drawBackground(time) {
            if (width < 2 || height < 2)
                return;
            ctx.clearRect(0, 0, width, height);
            const { coreX, coreY } = layout;
            const softRed = ctx.createRadialGradient(coreX, coreY, 30, coreX, coreY, width * 0.22);
            softRed.addColorStop(0, 'rgba(227, 23, 36, 0.075)');
            softRed.addColorStop(0.42, 'rgba(227, 23, 36, 0.026)');
            softRed.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = softRed;
            ctx.fillRect(0, 0, width, height);
            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            for (let i = 0; i < 5; i++) {
                const emitter = getEmitter(i);
                const gradient = ctx.createLinearGradient(emitter.x, emitter.y, coreX, coreY);
                gradient.addColorStop(0, 'rgba(227,23,36,0.02)');
                gradient.addColorStop(0.58, 'rgba(227,23,36,0.08)');
                gradient.addColorStop(1, 'rgba(227,23,36,0.025)');
                ctx.strokeStyle = gradient;
                ctx.lineWidth = 2.2;
                ctx.beginPath();
                ctx.moveTo(emitter.x, emitter.y);
                ctx.bezierCurveTo(coreX - width * 0.08, emitter.y + Math.sin(time / 3600 + i) * 10, coreX - width * 0.04, coreY + (i - 2) * 12, coreX, coreY);
                ctx.stroke();
            }
            ctx.restore();
        }
        function drawParticle(previousX, previousY, particle) {
            const fadeIn = Math.min(1, particle.progress * 8);
            const fadeOut = Math.min(1, (1 - particle.progress) * 5);
            const alpha = particle.alpha * fadeIn * fadeOut;
            ctx.strokeStyle = `rgba(227, 23, 36, ${0.18 * alpha})`;
            ctx.lineWidth = particle.size;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(previousX, previousY);
            ctx.lineTo(particle.x, particle.y);
            ctx.stroke();
            ctx.shadowColor = 'rgba(227, 23, 36, 0.36)';
            ctx.shadowBlur = 8;
            ctx.fillStyle = `rgba(227, 23, 36, ${0.74 * alpha})`;
            if (particle.index % 2 === 0) {
                ctx.beginPath();
                ctx.arc(particle.x, particle.y - 4, 1.6 + particle.size * 0.62, 0, Math.PI * 2);
                ctx.fill();
            }
            else {
                ctx.font = '700 11px SFMono-Regular, Consolas, monospace';
                ctx.fillText(particle.glyph, particle.x, particle.y - 5);
            }
            ctx.shadowBlur = 0;
        }
        function updateParticles() {
            if (width < 2 || height < 2)
                return;
            particles.forEach((particle) => {
                const previousX = particle.x;
                const previousY = particle.y;
                particle.progress += particle.speed;
                if (particle.progress >= 1) {
                    Object.assign(particle, createParticle(particle.index, false));
                }
                const eased = 1 - Math.pow(1 - particle.progress, 2.2);
                const wave = Math.sin(particle.phase + particle.progress * Math.PI * 2) * (1 - particle.progress) * 10;
                particle.x = particle.startX + (particle.targetX - particle.startX) * eased;
                particle.y = particle.startY + (particle.targetY - particle.startY) * eased + wave;
                drawParticle(previousX, previousY, particle);
            });
        }
        function getExtensionTargets(heroRect, coreY) {
            const endX = width - 14;
            const ui = hero.querySelector('.generated-ui');
            let arcY = coreY - Math.min(120, height * 0.14);
            if (ui) {
                const uiRect = ui.getBoundingClientRect();
                const uiTop = uiRect.top - heroRect.top;
                arcY = Math.min(coreY - 36, uiTop - 28);
            }
            return {
                endX,
                lanes: [
                    { endX, endY: arcY, c1: -16, c2: 8, alpha: 0.42 },
                    { endX: endX - width * 0.02, endY: coreY - height * 0.05, c1: -8, c2: 12, alpha: 0.34 },
                    { endX: endX - width * 0.045, endY: coreY + height * 0.04, c1: 22, c2: -6, alpha: 0.28 },
                ],
            };
        }
        function drawExtension(time) {
            if (width < 2 || height < 2)
                return;
            const { coreX, coreY } = layout;
            const heroRect = hero.getBoundingClientRect();
            const { lanes } = getExtensionTargets(heroRect, coreY);
            lanes.forEach((lane, laneIndex) => {
                const laneReach = Math.max(100, lane.endX - coreX);
                const gradient = ctx.createLinearGradient(coreX, coreY, lane.endX, lane.endY);
                gradient.addColorStop(0.12, `rgba(227,23,36,${lane.alpha})`);
                gradient.addColorStop(0.55, `rgba(227,23,36,${lane.alpha * 0.85})`);
                gradient.addColorStop(1, `rgba(227,23,36,${lane.alpha * 0.35})`);
                ctx.strokeStyle = gradient;
                ctx.lineWidth = laneIndex === 0 ? 2.1 : 1.5;
                ctx.setLineDash([4 + laneIndex, 7 + laneIndex * 2]);
                ctx.lineDashOffset = -time * 0.018 - laneIndex * 6;
                ctx.beginPath();
                ctx.moveTo(coreX, coreY + laneIndex * 3 - 4);
                ctx.bezierCurveTo(coreX + laneReach * 0.38, coreY + lane.c1, coreX + laneReach * 0.72, lane.endY + lane.c2, lane.endX, lane.endY);
                ctx.stroke();
                ctx.setLineDash([]);
            });
            for (let i = 0; i < 36; i++) {
                const lane = lanes[i % lanes.length];
                const t = ((i + 1) / 38 + time * 0.000055 + (i % 3) * 0.03) % 1;
                const drift = Math.sin(i * 1.73) * 10 + Math.cos(i * 0.91) * 5;
                const x = coreX + (lane.endX - coreX) * t + Math.sin(i * 2.17) * 7;
                const y = coreY + (lane.endY - coreY) * t + drift;
                const alpha = lane.alpha * (0.92 - t * 0.28);
                ctx.fillStyle = `rgba(227, 23, 36, ${alpha})`;
                ctx.beginPath();
                ctx.arc(x, y, 1.3 + (i % 4) * 0.38, 0, Math.PI * 2);
                ctx.fill();
            }
        }
        function animate(time) {
            drawBackground(time);
            drawExtension(time);
            updateParticles();
            rafId = requestAnimationFrame(animate);
        }
        resize();
        window.addEventListener('resize', resize);
        if (document.fonts?.ready) {
            document.fonts.ready.then(() => resize());
        }
        let ro = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => resize());
            ro.observe(hero);
            const stage = hero.querySelector('.product-stage-motion');
            const core = hero.querySelector('.product-core');
            const generatedUi = hero.querySelector('.generated-ui');
            if (stage)
                ro.observe(stage);
            if (core)
                ro.observe(core);
            if (generatedUi)
                ro.observe(generatedUi);
        }
        requestAnimationFrame(() => resize());
        rafId = requestAnimationFrame(animate);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', resize);
            if (ro)
                ro.disconnect();
        };
    }, []);
    return (_jsxs("section", { className: "hero", ref: heroRef, children: [_jsx("div", { className: "hero-corner-accent", "aria-hidden": "true" }), _jsx("canvas", { ref: canvasRef, className: "hero-motion-canvas", "aria-hidden": "true" }), _jsxs("div", { className: "page-shell hero-layout", children: [_jsxs("div", { className: "hero-content hero-content--original", children: [_jsx("div", { className: "hero-mark", "aria-hidden": "true" }), _jsx("h1", { children: _jsxs("span", { className: "hero-brand-lockup", children: [_jsx("img", { className: "hero-wordmark", alt: "YonCode", width: "1032", height: "171", src: "./portal/hero-wordmark.png" }), _jsxs("span", { className: "hero-title-line", children: ["\u4F01\u4E1A\u7EA7 AI Coding ", _jsx("span", { className: "hero-title-accent", children: "\u5E73\u53F0" })] })] }) }), _jsx("p", { className: "hero-lead hero-lede", children: "YonCode \u4E13\u6CE8\u6784\u5EFA AI \u539F\u751F\u4F01\u4E1A\u5E94\u7528\uFF0C\u6253\u901A\u9700\u6C42\u3001\u8BBE\u8BA1\u3001\u5F00\u53D1\u3001\u6D4B\u8BD5\u5230\u90E8\u7F72\u5168\u6D41\u7A0B\uFF0C\u5E2E\u52A9\u4F01\u4E1A\u7A33\u5B9A\u4EA4\u4ED8\u667A\u80FD\u5E94\u7528\u3002" }), _jsx("div", { className: "hero-actions", children: _jsx("a", { className: "button button-secondary", href: "#features", children: "\u4E86\u89E3\u4EA7\u54C1\u7279\u8272" }) })] }), _jsxs("div", { className: "product-stage product-stage-motion", "aria-label": "\u4EE3\u7801\u7C92\u5B50\u7A7F\u8FC7 YonCode \u4EA7\u54C1\u5BB9\u5668\u751F\u6210\u5E94\u7528\u754C\u9762\u7684\u6982\u5FF5\u52A8\u6548", children: [_jsx("div", { className: "motion-label label-input", children: "Code particles" }), _jsx("div", { className: "motion-label label-output", children: "YonCode UI" }), _jsxs("div", { className: "product-core", "aria-hidden": "true", children: [_jsx("div", { className: "core-ring" }), _jsxs("div", { className: "core-box", children: [_jsx("span", { children: "YonCode" }), _jsx("strong", { children: "Product Core" })] })] }), _jsx("div", { className: "generated-ui", children: _jsx("img", { className: "hero-product-shot__img", src: "./portal/hero-product-shot.png", alt: "YonCode IDE \u4E0E AI \u52A9\u624B\u754C\u9762", width: "2600", height: "1760", decoding: "async" }) })] })] })] }));
}
